import Segmented from '../shared/Segmented.js';
import {RuminantGroupLabel, ruminantTrend} from './ruminantPresentation.js';
import Button from '../shared/Button.js';
// Phase 2 Round 3 extraction (verbatim).
import React from 'react';
import type {ReactNode} from 'react';
import {openableProps} from '../shared/openable.js';
import {fmt, toISO, addDays, todayCentralISO} from '../lib/dateUtils.js';
import {buildForecast} from '../lib/cattleForecast.js';
import type {CattleProcessingBatchRow, ForecastWeighInRow} from '../lib/cattleForecast.js';
import {feedNutritionContribution} from '../lib/cattleNutrition.js';
import type {FeedInput, NutritionSnapshot} from '../lib/cattleNutrition.js';
import type {CattleRecord} from '../lib/cattleHerdFilters.js';
import {StatStrip} from '../shared/StatStrip.js';
import {groupColor} from '../shared/GroupPillPicker.js';
import InlineNotice from '../shared/InlineNotice.jsx';
import type {Notice} from '../shared/InlineNotice.jsx';
import {useUI} from '../contexts/UIContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {CattleHomeCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';
import './cattleRedesign.css';
import './cattleDashboardRedesign.css';

type CattleHomeCow = CattleRecord & {herd: string; purchase_date?: string | null};
type CattleHomeFeed = FeedInput & {
  is_creep?: boolean | null;
  nutrition_snapshot?: (NutritionSnapshot & {landed_per_lb?: number | string | null}) | null;
};
type CattleHomeDaily = {
  date: string;
  herd: string;
  mortality_count?: number | string | null;
  feeds?: CattleHomeFeed[] | null;
};
type CattleNutritionTarget = {
  herd: string;
  target_dm_pct_body?: number | string | null;
  target_cp_pct_dm?: number | string | null;
  target_nfc_pct_dm?: number | string | null;
};
type CattleHomeData = Omit<
  CattleHomeCoreData,
  'cattle' | 'dailys' | 'weighIns' | 'nutritionTargets' | 'processingBatches'
> & {
  cattle: CattleHomeCow[];
  dailys: CattleHomeDaily[];
  weighIns: ForecastWeighInRow[];
  nutritionTargets: CattleNutritionTarget[];
  processingBatches: CattleProcessingBatchRow[];
};
type NutritionValues = {dm: number; cp: number; nfc: number};
type CattleHerdWindow = {
  feedLbs: number;
  feedCost: number;
  mort: number;
  perDay: NutritionValues;
  target: NutritionValues | null;
  cowUnits: number;
  reportDays: number;
  days: number;
};
type CattleMetricItem = {
  l: string;
  v: string;
  sub?: string | null;
  pct?: number | null;
  trend?: ReactNode;
  warn?: boolean;
  color?: string;
};
type CattleMetricsGridProps = {s: CattleHerdWindow; prev: CattleHerdWindow | null};
type PeriodToggleProps = {
  val: number;
  setVal: (value: number) => void;
  opts: readonly {v: number; l: string}[];
};

const CATTLE_HOME_PARAMS = Object.freeze({});
const EMPTY_CATTLE_HOME_ROWS = Object.freeze([]) as readonly never[];

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as {message?: unknown}).message;
    return String(message || error);
  }
  return String(error);
}

const CattleHomeView = () => {
  const {setView} = useUI();
  const {useState} = React;
  const homeResource = useRouteResource(RESOURCE.CATTLE_HOME_CORE, CATTLE_HOME_PARAMS);
  const homeData = homeResource.data as CattleHomeData | null;
  const cattle = homeData ? homeData.cattle : EMPTY_CATTLE_HOME_ROWS;
  const dailys = homeData ? homeData.dailys : EMPTY_CATTLE_HOME_ROWS;
  const weighIns = homeData ? homeData.weighIns : EMPTY_CATTLE_HOME_ROWS;
  const targets = React.useMemo(() => {
    const targetMap: Record<string, CattleNutritionTarget> = {};
    (homeData ? homeData.nutritionTargets : []).forEach((row) => {
      targetMap[row.herd] = row;
    });
    return targetMap;
  }, [homeData]);
  const loading = homeResource.loading;
  const loadError: Notice | null = homeResource.error
    ? {
        kind: 'error',
        message: 'Could not load cattle dashboard. Please retry. (' + errorMessage(homeResource.error) + ')',
      }
    : null;
  const [cattleDashPeriod, setCattleDashPeriod] = useState<number>(30);
  const loadAll = homeResource.refetch;
  const HERDS = ['mommas', 'backgrounders', 'finishers', 'bulls'];
  const HERD_LABELS: Record<string, string> = {
    mommas: 'Mommas',
    backgrounders: 'Backgrounders',
    finishers: 'Finishers',
    bulls: 'Bulls',
  };
  const cutoff30 = toISO(addDays(todayCentralISO(), -30));
  // Forecast tile — next processor batch + current-year ready count.
  const forecastTile = React.useMemo(() => {
    if (!homeData || !homeData.forecastSettings) return null;
    try {
      const allBatches = homeData.processingBatches || [];
      const f = buildForecast({
        cattle,
        weighIns,
        settings: homeData.forecastSettings,
        includes: homeData.forecastIncludes,
        hidden: homeData.forecastHidden,
        realBatches: allBatches.filter((b) => b.status === 'active' || b.status === 'complete'),
        scheduledBatches: allBatches.filter((b) => b.status === 'scheduled'),
        todayMs: Date.now(),
      });
      return {nextBatch: f.nextProcessorBatch, currentYearReady: f.summary.readyThisYear};
    } catch {
      return null;
    }
  }, [homeData, cattle, weighIns]);

  // Weigh-ins sorted newest-first so .find() returns the globally latest
  // matching entry across whatever tags a cow has had (current + old).
  const weighInsDesc = React.useMemo(
    () => [...weighIns].sort((a, b) => (b.entered_at || '').localeCompare(a.entered_at || '')),
    [weighIns],
  );

  // Tags to probe for a given cow: current tag + any non-purchase old tags.
  // source='import' entries are selling-farm tags that can collide with
  // unrelated WCF cows and must NOT be used for weight lookups.
  function cowTagSet(c: CattleHomeCow): Set<string | number> {
    const s = new Set<string | number>();
    if (c.tag) s.add(c.tag);
    if (Array.isArray(c.old_tags)) {
      for (const ot of c.old_tags) {
        if (!ot || !ot.tag || ot.source === 'import') continue;
        s.add(ot.tag);
      }
    }
    return s;
  }

  function cowLastWeight(c: CattleHomeCow): number {
    const tags = cowTagSet(c);
    if (tags.size === 0) return 0;
    const w = weighInsDesc.find((x) => tags.has(x.tag as string | number));
    return w ? parseFloat(w.weight as string) || 0 : 0;
  }

  // Default for cows without a real weigh-in yet. Used in aggregate math
  // (herd live weight, cow units, nutrition target) so feed planning stays
  // sane between purchase and first real weigh-in. Per-cow displays still
  // show "no weigh-in" — this fallback is aggregate-only.
  //
  // Scope: only cows purchased within the last ESTIMATE_WINDOW_DAYS days.
  // Older un-weighed records (legacy cows, on-farm calves) contribute 0 —
  // 1,000 lb is wrong for a calf and dishonest for a long-standing data gap.
  const DEFAULT_COW_WEIGHT = 1000;
  const ESTIMATE_WINDOW_DAYS = 120;
  function isRecentlyPurchased(c: CattleHomeCow | null | undefined): boolean {
    if (!c || !c.purchase_date) return false;
    const ms = Date.now() - new Date(c.purchase_date + 'T12:00:00').getTime();
    return ms >= 0 && ms <= ESTIMATE_WINDOW_DAYS * 86400000;
  }
  function cowEffectiveWeight(c: CattleHomeCow): number {
    const real = cowLastWeight(c);
    if (real > 0) return real;
    return isRecentlyPurchased(c) ? DEFAULT_COW_WEIGHT : 0;
  }
  function isWeightEstimated(c: CattleHomeCow): boolean {
    return cowLastWeight(c) === 0 && isRecentlyPurchased(c);
  }

  // Herd live weight = sum of effective weights (real where known, 1000 lb
  // fallback for un-weighed cows). Drives the dashboard tiles + window math.
  function herdLiveWeight(herd: string): number {
    return cattle.filter((c) => c.herd === herd).reduce((s, c) => s + cowEffectiveWeight(c), 0);
  }
  function herdEstimatedCount(herd: string): number {
    return cattle.filter((c) => c.herd === herd && isWeightEstimated(c)).length;
  }

  const totalCattle = cattle.filter((c) => HERDS.includes(c.herd)).length;
  const totalLiveWeight = HERDS.reduce((s, h) => s + herdLiveWeight(h), 0);
  const totalEstimatedCows = HERDS.reduce((s, h) => s + herdEstimatedCount(h), 0);
  const dailys30 = dailys.filter((d) => (d.date || '') >= cutoff30);
  const totalMort30 = dailys30.reduce((s, d) => s + (parseInt(d.mortality_count as string) || 0), 0);
  // ── Rolling-window stats per herd ───────────────────────────────────────
  // Per-herd metrics for an arbitrary [fromISO, toISO] window. Target DM/day
  // = cow_units × target_pct × 10 (since total body weight = cow_units × 1000).
  // Cow units are window-AVERAGED, backdated to purchase_date so a cow
  // purchased mid-window only contributes to cow_units for the days she was
  // actually present — target lbs/day shrinks proportionally and matches
  // what was actually being fed. Creep-flagged feed lines on Mommas are
  // excluded from DM/CP/NFC (calves eat it, not the mommas) but still
  // counted in feedLbs / feedCost.
  function computeCattleHerdWindow(herd: string, fromISO: string, toISO: string): CattleHerdWindow {
    const windowDays = Math.max(
      1,
      Math.floor((new Date(toISO + 'T12:00:00').getTime() - new Date(fromISO + 'T12:00:00').getTime()) / 86400000) + 1,
    );
    const rows = dailys.filter((d) => d.herd === herd && d.date >= fromISO && d.date <= toISO);
    let dm = 0,
      cp = 0,
      nfc = 0,
      feedLbs = 0,
      feedCost = 0,
      mort = 0;
    const reportDates = new Set<string>();
    for (const d of rows) {
      reportDates.add(d.date);
      mort += parseInt(d.mortality_count as string) || 0;
      const feeds = Array.isArray(d.feeds) ? d.feeds : [];
      for (const f of feeds) {
        const ns = f.nutrition_snapshot || {};
        const cpl = parseFloat(ns.landed_per_lb as string) || 0;
        const nutrition = feedNutritionContribution(f);
        feedLbs += nutrition.asFedLbs;
        feedCost += nutrition.asFedLbs * cpl;
        if (herd === 'mommas' && f.is_creep) continue;
        dm += nutrition.dryMatterLbs;
        cp += nutrition.crudeProteinLbs;
        nfc += nutrition.nfcLbs;
      }
    }
    const perDay = {dm: dm / windowDays, cp: cp / windowDays, nfc: nfc / windowDays};

    let unitDays = 0;
    for (const c of cattle) {
      if (c.herd !== herd) continue;
      const wt = cowEffectiveWeight(c);
      let daysIn = windowDays;
      if (c.purchase_date && c.purchase_date > fromISO) {
        const ms = new Date(toISO + 'T12:00:00').getTime() - new Date(c.purchase_date + 'T12:00:00').getTime();
        daysIn = Math.max(0, Math.min(windowDays, Math.floor(ms / 86400000) + 1));
      }
      unitDays += wt * daysIn;
    }
    const cowUnits = unitDays / windowDays / 1000;

    const t = targets[herd];
    let target: NutritionValues | null = null;
    if (t && cowUnits > 0) {
      const targetDm = cowUnits * (parseFloat(t.target_dm_pct_body as string) || 0) * 10;
      target = {
        dm: targetDm,
        cp: (targetDm * (parseFloat(t.target_cp_pct_dm as string) || 0)) / 100,
        nfc: (targetDm * (parseFloat(t.target_nfc_pct_dm as string) || 0)) / 100,
      };
    }
    return {feedLbs, feedCost, mort, perDay, target, cowUnits, reportDays: reportDates.size, days: windowDays};
  }
  // Only show herds that actually have cows — empty herds just add noise.
  const activeHerdsWithCows = HERDS.filter((h) => cattle.some((c) => c.herd === h));

  // Keep the existing comparison window alongside the redesigned metrics.
  // Table D "Nutrition vs target": warn below 90%, danger below 60%, with the
  // word "Below Target"; at target or above the meter is plain green (D05/D08).
  function nutritionTone(pct: number): 'danger' | 'warn' | null {
    return pct < 60 ? 'danger' : pct < 90 ? 'warn' : null;
  }
  const CattleMetricsGrid = ({s, prev}: CattleMetricsGridProps) => {
    const targetPct = (value: number, target: number | undefined) =>
      target != null && target > 0 ? (value / target) * 100 : null;
    const items: CattleMetricItem[] = [
      {l: 'Total feed', v: s.feedLbs > 0 ? Math.round(s.feedLbs).toLocaleString() + ' lb' : '—'},
      {
        l: 'DM lb/day',
        v: s.perDay.dm > 0 ? s.perDay.dm.toFixed(1) : '—',
        sub: s.target ? Math.round(s.target.dm).toLocaleString() : null,
        pct: targetPct(s.perDay.dm, s.target?.dm),
        trend: prev ? ruminantTrend(s.perDay.dm, prev.perDay.dm) : null,
      },

      {l: 'Mortality', v: String(s.mort || 0), warn: s.mort > 0},
      {l: 'Report days', v: s.reportDays + ' / ' + s.days},
    ];
    return (
      <>
        <div className="cattle-metrics-grid">
          {items.map((it) => (
            <div className="cattle-metric" key={it.l}>
              <div className="cattle-eyebrow">{it.l}</div>
              <div className="cattle-metric-value" style={{color: it.warn ? 'var(--danger-ink)' : undefined}}>
                {it.v}
                {it.trend}
              </div>
              {it.pct != null &&
                Number.isFinite(it.pct) &&
                (() => {
                  // D08 meter: 6px gray track, green fill, "X of Y · Z%"; the
                  // fill turns warn/danger only past Table D's limits, with a word.
                  const tone = nutritionTone(it.pct);
                  return (
                    <div className="cattle-target-progress" data-nutrition-tone={tone || 'none'}>
                      <div className="cattle-progress-track" aria-hidden="true">
                        <div
                          style={{
                            width: Math.max(0, Math.min(100, it.pct)) + '%',
                            background: tone ? 'var(--' + tone + ')' : 'var(--brand)',
                          }}
                        />
                      </div>
                      <div className="cattle-target-caption">
                        <span>
                          {it.v} of {it.sub} lb/day · {Math.round(it.pct)}%
                        </span>
                        {tone && (
                          <span
                            style={{color: tone === 'danger' ? 'var(--danger)' : 'var(--warn-ink)', fontWeight: 700}}
                          >
                            Below Target
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()}
            </div>
          ))}
        </div>
        <div className="cattle-metric-details">
          CP {s.perDay.cp > 0 ? s.perDay.cp.toFixed(1) : '—'} lb/day
          {s.target ? ' / ' + Math.round(s.target.cp) + ' target' : ''}
          {' · '}NFC {s.perDay.nfc > 0 ? s.perDay.nfc.toFixed(1) : '—'} lb/day
          {s.target ? ' / ' + Math.round(s.target.nfc) + ' target' : ''}
          {' · '}Feed Cost {s.feedCost > 0 ? '$' + Math.round(s.feedCost).toLocaleString() : '—'}
        </div>
      </>
    );
  };
  const PeriodToggle = ({val, setVal, opts}: PeriodToggleProps) => (
    <Segmented
      aria-label="Herd Rolling Window"
      style={{width: 'fit-content'}}
      value={String(val)}
      onChange={(v) => setVal(Number(v))}
      options={opts.map(({v, l}) => ({value: String(v), label: l}))}
    />
  );

  // Windows are farm-time (America/Chicago) calendar days, so they roll over at
  // farm midnight rather than UTC midnight.
  const todayISOstr = todayCentralISO();
  const periodFromISO = toISO(addDays(todayISOstr, -(cattleDashPeriod - 1)));
  const prevToISO = toISO(addDays(todayISOstr, -cattleDashPeriod));
  const prevFromISO = toISO(addDays(todayISOstr, -(cattleDashPeriod * 2 - 1)));
  const lastWeighIn = weighInsDesc[0];
  const lastWeighInDate = lastWeighIn?.entered_at || lastWeighIn?.date || lastWeighIn?.entered_at_date;
  return (
    <div className="cattle-redesign" data-cattle-home-loaded={loading || loadError ? 'false' : 'true'}>
      <Header />
      <main className="cattle-page">
        <div>
          <h1 className="cattle-page-title">Cattle</h1>
          {!loading && !loadError && (
            <p className="cattle-page-summary">
              <strong>{totalCattle.toLocaleString()}</strong> on farm · <strong>{activeHerdsWithCows.length}</strong>{' '}
              active herds · last weigh-in {fmt(lastWeighInDate)}
            </p>
          )}
        </div>
        {loadError && (
          <div>
            <InlineNotice notice={loadError} />
            <Button
              variant="secondary"
              className="cattle-button"
              type="button"
              data-cattle-home-load-retry="1"
              onClick={loadAll}
            >
              Retry
            </Button>
          </div>
        )}
        {loading && (
          <div className="cattle-empty-state" role="status">
            Loading…
          </div>
        )}
        {!loading && !loadError && (
          <>
            {/* R15: one joined strip with hairline dividers, not four loose cards.
                The strip is four fixed tiles that fit 1180px, so the horizontal
                scroll region the old grid needed is gone with it. */}
            <StatStrip
              rootAttrs={{'data-cattle-home-strip': '1'}}
              tiles={[
                {label: 'Cattle on farm', value: totalCattle > 0 ? totalCattle.toLocaleString() : null},
                {
                  label: 'Total live weight',
                  value: totalLiveWeight > 0 ? Math.round(totalLiveWeight).toLocaleString() : null,
                  unit: totalLiveWeight > 0 ? 'lb' : undefined,
                  // R11: one sub-line, and only where the reader acts on it —
                  // how much of that weight is the 1,000 lb fallback.
                  sub: totalEstimatedCows > 0 ? totalEstimatedCows + ' est. @ 1,000 lb' : undefined,
                },
                {
                  label: 'Mortality 30d',
                  value: totalMort30 > 0 ? totalMort30.toLocaleString() : '0',
                  // R03: a soft tint, never a solid status fill.
                  tone: totalMort30 > 0 ? 'danger' : undefined,
                },
                {label: 'Reports 30d', value: dailys30.length > 0 ? dailys30.length.toLocaleString() : null},
              ]}
            />
            {/* R04: the one dark tile this page is allowed. The next processor
                batch is what the reader came to the dashboard for, so it takes
                the attention gradient and white type rather than a plain card. */}
            <section className="cattle-card cattle-next-processor cattle-attention" data-cattle-home-forecast-tile>
              <div className="cattle-calendar-tile" aria-hidden="true">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <rect x="3" y="5" width="18" height="16" rx="2" />
                  <path d="M3 10h18M8 3v4M16 3v4" />
                </svg>
              </div>
              <div className="cattle-next-info">
                <div className="cattle-eyebrow">Next to processor</div>
                <div className="cattle-next-value">
                  {forecastTile?.nextBatch ? (
                    <>
                      <strong>{forecastTile.nextBatch.name}</strong> · {forecastTile.nextBatch.label} ·{' '}
                      {forecastTile.nextBatch.animalIds.length}{' '}
                      {forecastTile.nextBatch.animalIds.length === 1 ? 'cow' : 'cows'} ·{' '}
                      {Math.round(forecastTile.nextBatch.projectedTotalLbs).toLocaleString()} lb projected
                    </>
                  ) : forecastTile ? (
                    'No planned batch yet'
                  ) : (
                    'Forecast unavailable'
                  )}
                </div>
              </div>
              <div className="cattle-next-actions">
                {forecastTile && (
                  <span>
                    <strong>{forecastTile.currentYearReady}</strong> ready this year
                  </span>
                )}
                <Button
                  variant="secondary"
                  type="button"
                  className="cattle-button"
                  onClick={() => setView('cattleforecast')}
                >
                  Open forecast →
                </Button>
              </div>
            </section>
            <section>
              <h2 className="cattle-dashboard-heading">Herd Breakdown</h2>
              <div className="cattle-herd-breakdown">
                {HERDS.map((h) => {
                  const cows = cattle.filter((c) => c.herd === h);
                  const lw = herdLiveWeight(h);
                  const est = herdEstimatedCount(h);
                  const t = targets[h];
                  // R06: the herd's own palette colour carries the card as a
                  // light tint, the same 6% the broiler active cards use. An
                  // empty herd stays neutral — there is nothing to colour.
                  const hue = groupColor(HERDS.indexOf(h));
                  return (
                    <div
                      key={h}
                      className="cattle-card cattle-herd-tile hoverable-tile"
                      {...openableProps(() => setView('cattleherds'))}
                      style={{
                        opacity: cows.length === 0 ? 0.55 : undefined,
                        background: cows.length > 0 ? 'color-mix(in srgb, ' + hue + ' 6%, var(--surface))' : undefined,
                      }}
                    >
                      <div className="cattle-herd-tile-title">
                        <strong>
                          <RuminantGroupLabel label={HERD_LABELS[h]} index={HERDS.indexOf(h)} />
                        </strong>
                        <span>
                          {cows.length} {cows.length === 1 ? 'cow' : 'cows'}
                        </span>
                      </div>
                      <div className="cattle-herd-values">
                        <div>
                          <div className="cattle-eyebrow">Live wt</div>
                          <strong>{lw > 0 ? Math.round(lw).toLocaleString() + ' lb' : '—'}</strong>
                        </div>
                        <div>
                          <div className="cattle-eyebrow">Cow units</div>
                          <strong>{lw > 0 ? (lw / 1000).toFixed(1) : '—'}</strong>
                        </div>
                      </div>
                      {est > 0 && <div className="cattle-cell-secondary">{est} est. @ 1,000 lb (no weigh-in yet)</div>}
                      <div className="cattle-herd-target">
                        {t
                          ? `Target · DM ${t.target_dm_pct_body ?? '—'}% · CP ${t.target_cp_pct_dm ?? '—'}% · NFC ${t.target_nfc_pct_dm ?? '—'}%`
                          : 'No target configured'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            {activeHerdsWithCows.length > 0 && (
              <>
                <div className="cattle-rolling-heading">
                  <h2 className="cattle-dashboard-heading">Herds — Rolling Window</h2>
                  <PeriodToggle
                    val={cattleDashPeriod}
                    setVal={setCattleDashPeriod}
                    opts={[
                      {v: 30, l: '30 Days'},
                      {v: 90, l: '90 Days'},
                      {v: 120, l: '120 Days'},
                    ]}
                  />
                </div>
                {activeHerdsWithCows.map((h) => {
                  const cur = computeCattleHerdWindow(h, periodFromISO, todayISOstr);
                  const prev = computeCattleHerdWindow(h, prevFromISO, prevToISO);
                  const t = targets[h];
                  const cows = cattle.filter((c) => c.herd === h);
                  const est = herdEstimatedCount(h);
                  return (
                    <section
                      key={h}
                      className="cattle-card wcf-clickable"
                      {...openableProps(() => setView('cattleherds'))}
                    >
                      <div className="cattle-section-header">
                        <h3>
                          <RuminantGroupLabel label={HERD_LABELS[h]} index={HERDS.indexOf(h)} />
                        </h3>
                        <span className="cattle-count">{cows.length}</span>
                        <span className="cattle-window-units">
                          {cur.cowUnits > 0 ? cur.cowUnits.toFixed(1) + ' cow units avg' : '— cow units'}
                          {est > 0 && ` · ${est} estimated @ 1,000 lb`}
                        </span>
                        <span className="cattle-section-meta">
                          {t
                            ? `Target DM ${t.target_dm_pct_body ?? '—'}% · CP ${t.target_cp_pct_dm ?? '—'}% DM · NFC ${t.target_nfc_pct_dm ?? '—'}% DM`
                            : 'No target configured'}
                        </span>
                      </div>
                      <CattleMetricsGrid s={cur} prev={prev} />
                    </section>
                  );
                })}
              </>
            )}
            {totalCattle === 0 && (
              <div className="cattle-card cattle-empty-state">
                No cattle on file yet. Open <strong>Herds</strong> in the sub-nav to add your first cow.
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default CattleHomeView;
